import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';

const STAGE = { width: 460, height: 360 };
const IMAGE = 132;
const GAP_FROM_IMAGE = 48; // clear space between image edge and part chip
const RADIUS = IMAGE / 2 + GAP_FROM_IMAGE + 36;

function normalizeParts(parts) {
  if (!Array.isArray(parts)) return [];
  return parts
    .map((part) => {
      if (typeof part === 'string') {
        return { name: part, quantity: 1 };
      }
      if (!part || !part.name) return null;
      return {
        name: String(part.name),
        quantity: Math.max(1, parseInt(part.quantity, 10) || 1),
        photoUrl: part.photoUrl || part.photo || null,
      };
    })
    .filter(Boolean);
}

function partPosition(index, count) {
  // Start from top, distribute evenly; slight ellipse so labels breathe horizontally
  const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
  return {
    x: Math.cos(angle) * RADIUS * 1.15,
    y: Math.sin(angle) * RADIUS,
  };
}

const reduceMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const HarvestSourceExplode = ({ photoUrl, itemCode, styleName, parts, partNames }) => {
  const items = normalizeParts(parts && parts.length ? parts : partNames);

  return (
    <div className="harvest-new__source">
      <div
        className="harvest-new__explode harvest-new__explode--react"
        style={{ width: 'min(100%, 460px)', height: STAGE.height }}
      >
        <motion.div
          className="harvest-new__source-img"
          initial={reduceMotion ? false : { opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          {photoUrl ? (
            <img src={photoUrl} alt={styleName || itemCode || 'Product'} />
          ) : (
            <span className="harvest-new__source-placeholder">No image</span>
          )}
        </motion.div>

        {items.map((part, index) => {
          const { x, y } = partPosition(index, items.length);
          const delay = reduceMotion ? 0 : 0.18 + index * 0.09;

          return (
            <motion.div
              key={`${part.name}-${index}`}
              className="harvest-new__part harvest-new__part--animated"
              initial={
                reduceMotion
                  ? false
                  : { opacity: 0, scale: 0.55, x: '-50%', y: '-50%' }
              }
              animate={{
                opacity: 1,
                scale: 1,
                x: `calc(-50% + ${x}px)`,
                y: `calc(-50% + ${y}px)`,
              }}
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { delay, duration: 0.7, ease: [0.16, 1, 0.3, 1] }
              }
            >
              {part.photoUrl ? (
                <img
                  className="harvest-new__part-thumb"
                  src={part.photoUrl}
                  alt={part.name}
                />
              ) : null}
              <span className="harvest-new__part-name">{part.name}</span>
              <span className="harvest-new__part-qty" title="Quantity">
                ×{part.quantity}
              </span>
            </motion.div>
          );
        })}
      </div>

      <div className="harvest-new__source-meta">
        <div className="cat-name">{itemCode}</div>
        {styleName ? <div className="cat-attrs">{styleName}</div> : null}
        {items.length > 0 ? (
          <div className="harvest-new__source-count">
            {items.length} part{items.length === 1 ? '' : 's'} to harvest
          </div>
        ) : null}
      </div>
    </div>
  );
};

HarvestSourceExplode.propTypes = {
  photoUrl: PropTypes.string,
  itemCode: PropTypes.string,
  styleName: PropTypes.string,
  parts: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      photoUrl: PropTypes.string,
      photo: PropTypes.string,
    })
  ),
  partNames: PropTypes.arrayOf(PropTypes.string),
};

HarvestSourceExplode.defaultProps = {
  photoUrl: null,
  itemCode: '',
  styleName: '',
  parts: [],
  partNames: [],
};

export default HarvestSourceExplode;
